import { FDRStatusQuery, FieldQueryOptions } from '@cibo/core'
import { useLocalState } from '@cibo/ui'
import { PropsWithChildren, ReactNode, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBatchedFields } from '../../../queries'
import { useFieldSelectionContext } from './FieldSelectionContext'
import { FieldSelectionProvider } from './FieldSelectionProvider'
import { HideFilters, PaginatedFieldsContext } from './PaginatedFieldsContext'

export const PAGE_SIZES = [10, 25, 50]

type PaginatedFieldsProviderProps = {
  hideFilters?: HideFilters
  children?: ReactNode
  persistKey?: string
}

const parseJsonFromSearchParams = (searchParam: string) => {
  if (!searchParam) return undefined
  try {
    return JSON.parse(searchParam)
  } catch (e) {
    console.error(`Error parsing search param ${searchParam}:`, e)
    return undefined
  }
}

const PaginatedFieldsProviderBase = ({
  children,
  hideFilters = {},
  persistKey,
}: PropsWithChildren<PaginatedFieldsProviderProps>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState<FieldQueryOptions>({})
  const [filtersInitialSet, setFiltersInitialSet] = useState(false)
  const [baseFilters, setBaseFilters] = useState<FieldQueryOptions>({})
  const [stateFilters, setStateFilters] = useLocalState<FieldQueryOptions>({}, persistKey)

  useEffect(() => {
    !!searchParams.get('page') && searchParams.set('page', '0')
    !!searchParams.get('pageSize') && searchParams.set('pageSize', '10')

    const newFilters = {
      owner: searchParams.get('owner') as string,
      fdrStatusAnyOf: parseJsonFromSearchParams(
        searchParams.get('fdrStatusAnyOf') as string
      ) as FDRStatusQuery[],
      ...stateFilters,
    }

    // Only update if filters actually changed to avoid infinite loops
    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      setFilters(newFilters)
      applyNewSearchParams(newFilters)
    }

    setFiltersInitialSet(true)
    setSearchParams(searchParams)
  }, [stateFilters])

  useEffect(() => {
    if (filtersInitialSet) {
      const ownerFromUrl = searchParams.get('owner') as string
      const ownerToUse = ownerFromUrl || stateFilters.owner
      const fdrStatusFromUrl = parseJsonFromSearchParams(
        searchParams.get('fdrStatusAnyOf') as string
      ) as FDRStatusQuery[]
      const fdrStatusToUse = fdrStatusFromUrl || stateFilters.fdrStatusAnyOf

      const newFilters: FieldQueryOptions = {
        owner: ownerToUse,
        fdrStatusAnyOf: fdrStatusToUse,
      }

      // If owner is missing from URL but present in localStorage, restore it
      if (ownerToUse && !ownerFromUrl) {
        // Reset page when restoring filters from localStorage
        searchParams.set('page', '0')
        // Use onSetFilters to ensure all side effects happen (URL params, localStorage, context update)
        onSetFilters(newFilters, false)
      }
    }
  }, [searchParams, stateFilters, filtersInitialSet])

  const applyNewSearchParams = (newFilters: FieldQueryOptions) => {
    if (newFilters.owner) {
      searchParams.set('owner', newFilters.owner.toString())
    }
    if (newFilters.fdrStatusAnyOf && typeof newFilters.fdrStatusAnyOf === 'object') {
      searchParams.set('fdrStatusAnyOf', JSON.stringify(newFilters.fdrStatusAnyOf))
    }
    ;(!newFilters.fdrStatusAnyOf ||
      newFilters.fdrStatusAnyOf === null ||
      newFilters.fdrStatusAnyOf.length === 0) &&
      searchParams.delete('fdrStatusAnyOf')
    !newFilters.owner && searchParams.delete('owner')
    // Always remove searchOption from URL params - it's determined by permission, not user selection
    searchParams.delete('searchOption')
    setSearchParams(searchParams)
  }

  const onSetFilters = (newFilters: FieldQueryOptions, doSetStateFilters = true) => {
    if (doSetStateFilters) {
      setStateFilters(newFilters)
    }
    setFiltersInitialSet(true)
    setFilters(newFilters)
    applyNewSearchParams(newFilters)
  }

  const { selectedRowIds } = useFieldSelectionContext()

  const fields = useBatchedFields(selectedRowIds as string[])

  return (
    <PaginatedFieldsContext.Provider
      value={{
        filters,
        setFilters: onSetFilters,
        hideFilters,
        baseFilters,
        setBaseFilters,

        isReady: !fields.isPending,

        page: searchParams.get('page') ? parseInt(searchParams.get('page') as string, 10) : 0,
        setPage: (newPage: number) => {
          searchParams.set('page', newPage.toString())
          setSearchParams(searchParams)
        },

        pageSize: searchParams.get('pageSize')
          ? parseInt(searchParams.get('pageSize') as string, 10)
          : PAGE_SIZES[0],
        setPageSize: (newPageSize: number) => {
          searchParams.set('pageSize', newPageSize.toString())
          setSearchParams(searchParams)
        },
      }}
    >
      {children}
    </PaginatedFieldsContext.Provider>
  )
}

export const PaginatedFieldsProvider = (props: PaginatedFieldsProviderProps) => {
  return (
    <FieldSelectionProvider>
      <PaginatedFieldsProviderBase {...props} />
    </FieldSelectionProvider>
  )
}
